import { useCallback, useState } from "react";
import { axios } from "@/redux/axios";
import Currency from "@/components/etc/currency";
import moment from "moment";
import { GetSMSThunk, ResendSMSThunk } from "@/redux/features/actions/sms";
import { useAppDispatch, useAppSelector } from "@/redux/hook";
import { IconExclamationCircle, IconEye } from "@tabler/icons-react";
import { Modal } from "antd";
import Button from "@/components/button";
import { toast } from "react-toastify";
import { GetTransactionsThunk } from "@/redux/features/actions/transactions";
import { ResendTokenButton } from "../transactions/resendToken";

const PlayerTransactionPage = () => {
  const dispatch = useAppDispatch();
  const { list: smsList } = useAppSelector((s) => s.sms);
  

  const [phone, setPhone] = useState("");
  const [activeTab, setActiveTab] = useState<"tokens" | "transactions"|"sms" | "personal"  >(
    "transactions",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [tokens, setTokens] = useState<any[]>([]);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [selectedSMS, setSelectedSMS] = useState<any>(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [smsToResend, setSmsToResend] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!phone) return;
  
    setIsLoading(true);
    setError(null);
    setTransactions([]);
    setTokens([]);
    setUserDetails(null);
  
    try {
      // Make parallel requests
      const [txnResponse,SMSResponse,transactionsResponse,] = await Promise.all([
        axios.get("/v1/transactions/getTokenByPhoneNumber", {
          params: { phoneNumber: phone },
        }),
        dispatch(GetSMSThunk({ recipient: phone })),
        dispatch(GetTransactionsThunk({ 
          search:phone,
          pagination: { pageSize: 100 }
        })),
      ]);
      console.log(SMSResponse)
  
      // Process tokens from the first endpoint
      const txnData = txnResponse.data;
      const extractedTokens = txnData.map((item: any) => item.token).filter(Boolean);
      setTokens(extractedTokens);
  
     
      const transactionsData = transactionsResponse.payload?.list || [];
      setTransactions(transactionsData);
      console.log(transactionsResponse)
      // Set user details
      if (transactionsData.length > 0 || extractedTokens.length > 0) {
        setUserDetails({
          phone: transactionsData[0]?.phone_number || phone,
        });
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };
  const truncateMessage = (message: string, length: number = 30) => {
    return message.length > length ? `${message.substring(0, length)}...` : message;
  };

  const handleResend = useCallback(() => {
    if (smsToResend) {
      dispatch(ResendSMSThunk(smsToResend));
      toast.success("SMS resent successfully!");
      setSmsToResend(null);
    }
  }, [dispatch, smsToResend]);


  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-8">Player Information</h1>

      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <input
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Enter phone number"
          className="border border-primary/30 p-3 rounded-md w-full focus:ring-1 focus:ring-primary focus:outline-none"
        />
        <button
          onClick={handleSearch}
          disabled={isLoading}
          className="bg-primary border-none hover:bg-primary-dark text-white font-medium px-6 py-3 rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? "Searching..." : "Search"}
        </button>
      </div>

      <div className="md:flex">
        {/* Tabs */}
        <div className="flex flex-col space-y-4 text-sm font-medium text-gray-500 md:mr-4 mb-4 md:mb-0 w-full md:w-48">
          {["transactions", "tokens", "sms", "personal", ].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              disabled={transactions.length === 0 && tokens.length === 0}
              className={`inline-flex items-center border-none px-4 py-3 rounded-lg w-full ${
                activeTab === tab
                  ? "text-white bg-primary"
                  : "hover:text-gray-900 bg-gray-50 hover:bg-gray-100"
              } ${transactions.length === 0 && tokens.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <svg className="w-4 h-4 mr-2" />
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 bg-gray-50 text-medium text-gray-500 rounded-lg w-full">
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          )}

          {!isLoading && transactions.length === 0 && tokens.length === 0 && !error && (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-700">Search for a player</h3>
              <p className="text-gray-500 mt-1">Enter a phone number to view player information</p>
            </div>
          )}

          {(transactions.length > 0 || tokens.length > 0) && (
            <>
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {activeTab === "transactions" && "Transaction History"}
                {activeTab === "tokens" && "Token Information"}
                {activeTab === "personal" && "Personal Information"}
              </h3>
              {activeTab === "transactions" && (
  <div className="rounded-lg bg-white p-4 overflow-x-auto shadow-sm">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Telco
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Date
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Amount
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Product
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {transactions
          .map((txn: any, index: number) => (
            <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {txn.telco || "N/A"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {txn.created_at
                  ? moment(txn.created_at).format("lll")
                  : "N/A"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {txn.amount ? <Currency amount={Number(txn.amount)} /> : "N/A"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    txn.status === "SUCCESS"
                      ? "bg-green-100 text-green-800"
                      : txn.status === "FAILED"
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {txn.status || "N/A"}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {txn.product?.productName || "N/A"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <ResendTokenButton transaction={txn}/>
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  </div>
)}

              {activeTab === "tokens" && (
                <div className="rounded-lg bg-white p-4 overflow-x-auto shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Token ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created At
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tokens
                        .sort((a, b) => {
                          // Sort by 'created_at' in descending order
                          return (
                            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                          );
                        })
                        .map((token, index) => (
                          <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {token.tokenId || "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {token.createdAt
                                ? moment(token.createdAt).format("lll")
                                : "N/A"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === "sms" && (
                <div className="rounded-lg bg-white p-4 overflow-x-auto shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Message
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date Sent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {[...smsList]
                        ?.sort(
                          (a, b) =>
                            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                        )
                        ?.map((sms, index) => (
                          <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {sms.recipient || "N/A"}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div
                                className="flex items-center gap-2 cursor-pointer hover:text-primary"
                                onClick={() => {
                                  setSelectedSMS(sms);
                                  setIsDetailModalVisible(true);
                                }}
                              >
                                <IconEye />
                                <span title="Click to view full message">
                                  {truncateMessage(sms.message, 50)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {sms.created_at
                                ? moment(sms.created_at).format("lll")
                                : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <button
                                onClick={() => setSmsToResend(sms.id)}
                                className="bg-primary font-thin border-none px-6 hover:bg-primary-dark text-white text-xs py-2 rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Resend
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "personal" && (
                <div className="rounded-lg bg-white p-4 overflow-x-auto shadow-sm">
                  {userDetails ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr className="bg-white">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 w-1/3">
                            Phone Number
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {userDetails.phone || "N/A"}
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            Total Transactions
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{transactions.length}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">Tokens</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{tokens.length}</td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-gray-500">No player details available</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        title="SMS Details"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
           footer={
                 <Button onClick={() => setIsDetailModalVisible(false)}>
                   Close
                 </Button>
               }
        width={700}
      >
        {selectedSMS && (
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500">Recipient</p>
                <p className="font-medium">{selectedSMS.recipient}</p>
              </div>
              <div>
                <p className="text-gray-500">Date Sent</p>
                <p className="font-medium">
                  {moment(selectedSMS.created_at).format("LLL")}
                </p>
              </div>
              {selectedSMS.token && (
                <div>
                  <p className="text-gray-500">Token</p>
                  <p className="font-medium">{selectedSMS.token}</p>
                </div>
              )}
            </div>
            <div>
              <p className="text-gray-500">Full Message</p>
              <div className="p-3 bg-gray-50 rounded-md mt-1 whitespace-pre-wrap">
                {selectedSMS.message}
              </div>
            </div>
            {selectedSMS.status_message && (
              <div>
                <p className="text-gray-500">Status Message</p>
                <p className="font-medium">{selectedSMS.status_message}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <IconExclamationCircle className="text-yellow-500" />
            <span>Confirm Resend</span>
          </div>
        }
        open={!!smsToResend}
        onOk={handleResend}
        onCancel={() => setSmsToResend(null)}
        okText="Yes, Resend"
        cancelText="Cancel"
        okButtonProps={{
          className: "!bg-primary !border-primary hover:!bg-primary-dark",
        }}
      >
        <p>Are you sure you want to resend this SMS message?</p>
        <p className="text-gray-500 text-sm mt-2">
          This will attempt to send the message again to the recipient.
        </p>
      </Modal>
    </div>
  );
};

{
  /* Message Detail Modal */
}

export default PlayerTransactionPage;
